import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useEffect, useMemo, useCallback } from 'react';
import {
	REPORT_CATALOG_QUERY,
	type ReportCatalogQueryResponse,
	type ReportCatalogGroup
} from './reports.gql';
import { Loader2 } from 'lucide-react';
import {
	DollarSign,
	Settings2,
	Users,
	BarChart3,
	FileText
} from 'lucide-react';

const GROUP_ICON_MAP: Record<string, React.ComponentType<any>> = {
	financial: DollarSign,
	operations: Settings2,
	clients: Users
};

const getGroupIcon = (iconToken: string | null, groupKey: string) => {
	if (iconToken && GROUP_ICON_MAP[iconToken]) {
		return GROUP_ICON_MAP[iconToken];
	}
	if (GROUP_ICON_MAP[groupKey]) {
		return GROUP_ICON_MAP[groupKey];
	}
	return BarChart3;
};

export function ReportsCatalog() {
	const { t } = useTranslation('reports');
	const navigate = useNavigate();
	const { setPageHeader, resetPageHeader } = usePageHeader();

	useEffect(() => {
		setPageHeader({
			title: t('catalog.pageTitle'),
			description: t('catalog.pageDescription')
		});
		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, t]);

	const { data, loading, error } = useQuery<ReportCatalogQueryResponse>(
		REPORT_CATALOG_QUERY,
		{ fetchPolicy: 'cache-and-network' }
	);

	const sortedGroups = useMemo(() => {
		if (!data?.reportCatalog) return [];
		return [...data.reportCatalog].sort(
			(a, b) => a.sortOrder - b.sortOrder
		);
	}, [data]);

	const handleReportClick = useCallback(
		(reportKey: string) => {
			navigate(`/reports/${reportKey}`);
		},
		[navigate]
	);

	if (loading && !data) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-blue-500" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-64 items-center justify-center">
				<div className="text-center">
					<p className="text-lg font-medium text-destructive">
						{t('catalog.errorLoading')}
					</p>
					<p className="mt-1 text-sm text-muted-foreground">
						{error.message}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-5xl px-6 py-8">
			{/* Title */}
			<div className="mb-8">
				<h1 className="text-2xl font-semibold text-foreground">
					{t('catalog.title')}
				</h1>
				<p className="mt-1 text-sm text-muted-foreground">
					{t('catalog.subtitle')}
				</p>
			</div>

			{/* Groups */}
			<div className="space-y-10">
				{sortedGroups.map(group => (
					<ReportGroupSection
						key={group.reportGroupId}
						group={group}
						onReportClick={handleReportClick}
					/>
				))}
			</div>
		</div>
	);
}

interface ReportGroupSectionProps {
	group: ReportCatalogGroup;
	onReportClick: (reportKey: string) => void;
}

function ReportGroupSection({ group, onReportClick }: ReportGroupSectionProps) {
	const Icon = getGroupIcon(group.icon, group.key);
	const sortedReports = useMemo(
		() => [...group.reports].sort((a, b) => a.sortOrder - b.sortOrder),
		[group.reports]
	);

	return (
		<section>
			{/* Group header */}
			<div className="mb-4 flex items-center gap-2">
				<Icon className="h-5 w-5 text-muted-foreground" />
				<h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
					{group.title}
				</h2>
			</div>

			{/* Report cards */}
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				{sortedReports.map(report => (
					<button
						key={report.reportDefinitionId}
						type="button"
						onClick={() => onReportClick(report.key)}
						className="group flex flex-col rounded-xl border border-border bg-card p-5 text-left transition-all duration-200 hover:border-primary/30 hover:bg-accent hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						<div className="flex items-start gap-3">
							<FileText className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
							<div>
								<h3 className="text-sm font-semibold text-foreground">
									{report.title}
								</h3>
								<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
									{report.description}
								</p>
							</div>
						</div>
					</button>
				))}
			</div>
		</section>
	);
}
